import type { AnimeTitle, AnimeEpisode, AnimeServer } from './types.ts';
import { getDownloadFolder } from '../storage/DownloadFolder.ts';
import { cleanFilename } from '../storage/LocalChapters.ts';
import { yomiriDatabase } from '../storage/YomiriDatabase.ts';
import { streamVideoToDisk } from '../network/transports/DirectTransport.ts';

export interface LocalAnimeDownload {
  key: string; anime: AnimeTitle; episode: AnimeEpisode; bytes: number; savedAt: number;
  file: FileSystemFileHandle;
}
export const listAnimeDownloads = async () => await yomiriDatabase.getSetting<LocalAnimeDownload[]>('anime_downloads', []) || [];
export const isDirectVideo = (server: AnimeServer) => server.delivery === 'direct' && !server.id.startsWith('dl:');

export async function saveAnimeEpisode(anime: AnimeTitle, episode: AnimeEpisode, server: AnimeServer,
  signal: AbortSignal, progress: (bytes: number, total: number) => void): Promise<void> {
  if (!isDirectVideo(server)) throw new Error('Elegí un servidor con archivo directo. Los reproductores externos y HLS se abren desde su sitio.');
  const selected = await yomiriDatabase.getSetting('custom_directory_handle');
  if (!selected) {
    // Stage to browser-managed disk rather than buffering an episode in RAM.
    const storage = await navigator.storage.getDirectory();
    const temporary = `yomiri-download-${crypto.randomUUID()}.part`;
    const handle = await storage.getFileHandle(temporary, {create:true});
    try {
      await streamVideoToDisk(server.url, await handle.createWritable(), signal, progress);
      signal.throwIfAborted();
      const url = URL.createObjectURL(await handle.getFile());
      const link = document.createElement('a');
      link.href=url;link.download=cleanFilename(`Yomiri - ${anime.title} - Episodio ${episode.episodeNumber}`)+'.mp4';
      document.body.append(link);link.click();link.remove();
      setTimeout(()=>{URL.revokeObjectURL(url);void storage.removeEntry(temporary).catch(()=>{});},60000);
    } catch(error) {await storage.removeEntry(temporary).catch(()=>{});throw error;}
    return;
  }
  const root = await getDownloadFolder();
  const animeRoot = await root.getDirectoryHandle('Anime', { create: true });
  const directory = await animeRoot.getDirectoryHandle(cleanFilename(anime.title), { create: true });
  const filename = cleanFilename(`Episodio ${episode.episodeNumber}`) + '.mp4';
  // Keep older downloads untouched if this request fails or is canceled.
  const temporary = `${crypto.randomUUID()}.part`;
  const partial = await directory.getFileHandle(temporary, { create: true });
  try {
    const bytes = await streamVideoToDisk(server.url, await partial.createWritable(), signal, progress);
    signal.throwIfAborted();
    const file = await directory.getFileHandle(filename, { create: true });
    const writable = await file.createWritable();
    try { await writable.write(await partial.getFile()); await writable.close(); }
    catch (error) { await writable.abort().catch(() => {}); throw error; }
    const key = JSON.stringify([anime.sourceId, episode.id]);
    const rows = await listAnimeDownloads();
    await yomiriDatabase.setSetting('anime_downloads', [...rows.filter(row => row.key !== key), { key, anime, episode, file, bytes, savedAt: Date.now() }]);
    window.dispatchEvent(new Event('yomiri-downloads-changed'));
  } finally { await directory.removeEntry(temporary).catch(() => {}); }
}
